package org.catharsis.spendtel.feature.share

import kotlinx.browser.document
import kotlinx.browser.window
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.legal.LegalConstants
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement

actual fun renderShareImageCard(card: ShareImageCard): ByteArray =
    WebShareImageRenderer.render(card)

private object WebShareImageRenderer {
    private const val WIDTH = 1080
    private const val HEIGHT = 1350

    fun render(card: ShareImageCard): ByteArray {
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = WIDTH
        canvas.height = HEIGHT

        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
        drawBackground(ctx)

        var y = 120.0
        y = drawText(ctx, LegalConstants.PRODUCT_NAME.uppercase(), 72.0, y, "#EDE9FE", "600 28px sans-serif") + 24.0

        when (card) {
            is ShareImageCard.Highlight -> {
                y = drawText(ctx, "Spotlight", 72.0, y, "#FBBF24", "700 24px sans-serif") + 16.0
                y = drawWrappedText(
                    ctx,
                    card.text,
                    72.0,
                    y,
                    WIDTH - 144.0,
                    "#FFFFFF",
                    "700 52px sans-serif",
                    62.0,
                )
            }

            is ShareImageCard.Equivalence -> {
                val equivalence = card.equivalence
                y = drawText(
                    ctx,
                    equivalence.sourceLabel.uppercase(),
                    72.0,
                    y,
                    "#FBBF24",
                    "700 24px sans-serif",
                ) + 16.0
                y = drawText(
                    ctx,
                    formatCurrency(equivalence.amount, card.currency),
                    72.0,
                    y,
                    "#FFFFFF",
                    "700 52px sans-serif",
                ) + 20.0
                y = drawWrappedText(
                    ctx,
                    equivalence.comparison,
                    72.0,
                    y,
                    WIDTH - 144.0,
                    "#FFFFFF",
                    "700 52px sans-serif",
                    58.0,
                )
                if (equivalence.insight.isNotBlank()) {
                    y += 16.0
                    drawWrappedText(
                        ctx,
                        equivalence.insight,
                        72.0,
                        y,
                        WIDTH - 144.0,
                        "#FFFFFF",
                        "400 34px sans-serif",
                        44.0,
                        alpha = 0.9,
                    )
                }
            }

            is ShareImageCard.Personality -> {
                y = drawText(
                    ctx,
                    "Financial personality",
                    72.0,
                    y,
                    "#FBBF24",
                    "700 24px sans-serif",
                ) + 16.0
                y = drawWrappedText(
                    ctx,
                    card.personality.label,
                    72.0,
                    y,
                    WIDTH - 144.0,
                    "#FFFFFF",
                    "700 52px sans-serif",
                    58.0,
                )
                if (card.personality.description.isNotBlank()) {
                    y += 16.0
                    drawWrappedText(
                        ctx,
                        card.personality.description,
                        72.0,
                        y,
                        WIDTH - 144.0,
                        "#FFFFFF",
                        "400 34px sans-serif",
                        44.0,
                        alpha = 0.9,
                    )
                }
            }
        }

        drawText(
            ctx,
            "Generated with ${LegalConstants.PRODUCT_NAME}",
            72.0,
            HEIGHT - 120.0,
            "#EDE9FE",
            "600 24px sans-serif",
        )
        drawText(
            ctx,
            LegalConstants.WEBSITE.removePrefix("https://"),
            72.0,
            HEIGHT - 72.0,
            "#EDE9FE",
            "400 24px sans-serif",
            alpha = 0.65,
        )

        return pngBytesFromDataUrl(canvas.toDataURL("image/png"))
    }

    private fun drawBackground(ctx: CanvasRenderingContext2D) {
        val gradient = ctx.createLinearGradient(0.0, 0.0, WIDTH.toDouble(), HEIGHT.toDouble())
        gradient.addColorStop(0.0, "#5B21B6")
        gradient.addColorStop(0.5, "#312E81")
        gradient.addColorStop(1.0, "#1E1B4B")
        ctx.fillStyle = gradient
        ctx.fillRect(0.0, 0.0, WIDTH.toDouble(), HEIGHT.toDouble())
    }

    private fun drawText(
        ctx: CanvasRenderingContext2D,
        text: String,
        x: Double,
        startY: Double,
        color: String,
        font: String,
        alpha: Double = 1.0,
    ): Double {
        ctx.save()
        ctx.globalAlpha = alpha
        ctx.fillStyle = color
        ctx.font = font
        ctx.asDynamic().textBaseline = "top"
        ctx.fillText(text, x, startY)
        val metrics = ctx.measureText(text)
        val height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent + 8.0
        ctx.restore()
        return startY + height
    }

    private fun drawWrappedText(
        ctx: CanvasRenderingContext2D,
        text: String,
        x: Double,
        startY: Double,
        maxWidth: Double,
        color: String,
        font: String,
        lineHeight: Double,
        alpha: Double = 1.0,
    ): Double {
        ctx.font = font
        val lines = wrapLines(ctx, text, maxWidth)
        var y = startY
        lines.forEach { line ->
            y = drawText(ctx, line, x, y, color, font, alpha) + (lineHeight - 52.0)
        }
        return y
    }

    private fun wrapLines(
        ctx: CanvasRenderingContext2D,
        text: String,
        maxWidth: Double,
    ): List<String> {
        val words = text.split(" ")
        if (words.isEmpty()) return emptyList()

        val lines = mutableListOf<String>()
        var current = StringBuilder()

        words.forEach { word ->
            val candidate = if (current.isEmpty()) word else "${current} $word"
            if (ctx.measureText(candidate).width <= maxWidth) {
                if (current.isNotEmpty()) current.append(' ')
                current.append(word)
            } else {
                if (current.isNotEmpty()) {
                    lines.add(current.toString())
                    current = StringBuilder(word)
                } else {
                    lines.add(word)
                }
            }
        }

        if (current.isNotEmpty()) {
            lines.add(current.toString())
        }

        return lines
    }

    private fun pngBytesFromDataUrl(dataUrl: String): ByteArray {
        val base64 = dataUrl.substringAfter(",")
        val binaryString = window.atob(base64)
        val bytes = ByteArray(binaryString.length)
        for (index in bytes.indices) {
            bytes[index] = binaryString[index].code.toByte()
        }
        return bytes
    }
}
